import {useTranslation} from 'react-i18next';
import './quick-access.professional-caregiver.scss';
import {useMemo} from 'react';
import {
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  tableCellClasses,
} from '@mui/material';
import {FiEdit} from 'react-icons/fi';
import {MdOutlineDesktopAccessDisabled} from 'react-icons/md';
import {translations} from '../../../../../../i18n/translations';
import {tableSx} from '../../../../../../theme/table-sx';
import moment from 'moment';
import {TextCell} from '../../../../../../components/task-table/cells';
import {ComboActions} from '../../../../../../components/combo-actions/combo-actions';
import {Device} from '../../../../../../backend/interfaces/device';
import {AffiliatedUser} from '../../../../../../backend/responses/affiliated-user';
import {UserCell} from '../../occurrences/user';
import {QuickAccessData} from '../../../../../../interfaces/quick-access-data';
import {useAuth} from '../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';

type ColumnsId = 'name' | 'type' | 'access' | 'professional' | 'buttons';

type QuickAccessTableProps = {
  data?: Device[];
  professionals?: AffiliatedUser[];
  isLoading: boolean;
  canWrite: boolean;
  quickAccessData?: QuickAccessData;
  onEdit: (deviceId: string) => void;
  onDeactivate: (deviceId: string) => void;
};

export default function QuickAccessTable({
  data,
  professionals,
  isLoading,
  canWrite,
  quickAccessData,
  onEdit,
  onDeactivate,
}: QuickAccessTableProps) {
  const {t} = useTranslation();
  const {hasWritePermission} = useAuth();
  let columns: Record<
    ColumnsId,
    {
      label: string;
      sortable: boolean;
      sx?: SxProps<Theme>;
    }
  > = {
    name: {
      label: t(translations.office.professional_caregiver.settings.quick_access.name),
      sortable: false,
      sx: {
        minWidth: '100px',
        maxWidth: '193px',
      },
    },
    type: {
      label: t(translations.office.professional_caregiver.settings.quick_access.type),
      sortable: false,
      sx: {
        minWidth: '80px',
        maxWidth: '133px',
      },
    },
    access: {
      label: t(translations.office.professional_caregiver.settings.quick_access.access),
      sortable: false,
      sx: {
        minWidth: '80px',
        maxWidth: {xs: '243px', xl: '443px'},
      },
    },
    professional: {
      label: t(translations.office.professional_caregiver.settings.quick_access.professional),
      sortable: false,
      sx: {
        minWidth: '100px',
        maxWidth: {xs: '138px', xl: '138px'},
      },
    },
    buttons: {
      label: '',
      sortable: false,
      sx: canWrite ? undefined : {display: 'none'},
    },
  };

  const columnsData = useMemo(() => {
    const mappedData = data?.map((row) => {
      let professional = professionals?.find((user) => user.user.id === row.lastAccessedByUserId);
      if (!professional) {
        professional = professionals?.find((user) => user.user.id === row.registeredByUserId);
      }
      return {
        name: {value: row.number, picture: '', row},
        type: {
          value: t(translations.device_types[row.type]),
          picture: '',
          row,
        },
        access: {
          value: row.lastAccessedAt || row.updatedAt?.toString() || '',
          picture: '',
          row,
        },
        professional: {
          value: professional?.user.name || '',
          picture: professional?.user.picture,
          row,
        },
      };
    });

    return mappedData;
  }, [data, t, professionals]);

  return (
    <TableContainer component={Paper} sx={{...tableSx.container, marginTop: '0'}}>
      <Table stickyHeader sx={tableSx.table}>
        <TableHead>
          <TableRow
            sx={{
              [`& .${tableCellClasses.root}`]: {
                ...tableSx.header[`& .${tableCellClasses.root}`],
                fontWeight: '500',
              },
            }}
          >
            {Object.entries(columns).map(([key, {label, sx}], index) => {
              const columnId = key as ColumnsId;
              return (
                <TableCell key={`cell_for_${columnId}_${index}`} sx={sx}>
                  <span className="care-table-header-cell-label">{label}</span>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6}>
                {new Array(6).fill(1).map((_item, index) => (
                  <Skeleton key={`skeleton_${index}`} animation="wave" height={40} />
                ))}
              </TableCell>
            </TableRow>
          ) : (
            columnsData?.map((tableRow) => {
              return (
                <TableRow
                  key={`table_row_${tableRow['name'].row.id}`}
                  sx={{
                    ...tableSx.row,
                    backgroundColor:
                      tableRow['name'].row.id === quickAccessData?.device.id
                        ? 'var(--dcm-blue-hover)'
                        : undefined,
                    borderRadius:
                      tableRow['name'].row.id === quickAccessData?.device.id ? '5px' : undefined,
                    '& .MuiTableCell-root': {
                      ...tableSx.row['& .MuiTableCell-root'],
                      height: 'auto',
                    },
                    '& .care-table-row-cell': {
                      height: 'auto',
                    },
                  }}
                >
                  {Object.entries(tableRow).map(([key, cell]) => {
                    const columnId = key as ColumnsId;

                    if (columnId === 'professional') {
                      return (
                        <TableCell sx={columns[columnId].sx} key={`table_cell_${columnId}`}>
                          <UserCell name={cell.value || ''} picture={cell.picture} />
                        </TableCell>
                      );
                    }
                    const value = !cell.value
                      ? ''
                      : columnId === 'access'
                        ? `${moment(cell.value).format('L')} ${moment(cell.value).format('LT')}`
                        : cell.value;
                    return (
                      <TableCell
                        sx={columns[columnId].sx}
                        key={`table_cell_${columnId}`}
                        title={columnId === 'name' ? value.toString() : undefined}
                      >
                        <TextCell value={value.toString()} />
                      </TableCell>
                    );
                  })}
                  {canWrite && (
                    <TableCell key="buttons" sx={{position: 'relative'}}>
                      <ComboActions
                        {...{
                          actions: [
                            {
                              label: t(
                                translations.office.professional_caregiver.settings.templates.edit
                              ),
                              icon: {element: FiEdit},
                              disabled: !hasWritePermission(
                                RolePermissionType.SettingsQuickAccessDevices
                              ),
                              disabledTooltip: t(
                                translations.office.professional_caregiver.settings.quick_access
                                  .no_permissions
                              ),
                              callback: () => {
                                if (tableRow['name'].row.id) onEdit(tableRow['name'].row.id);
                              },
                            },
                            {
                              label: t(
                                translations.office.professional_caregiver.settings.quick_access
                                  .deactivate
                              ),
                              icon: {element: MdOutlineDesktopAccessDisabled},
                              disabled: !hasWritePermission(
                                RolePermissionType.SettingsQuickAccessDevices
                              ),
                              disabledTooltip: t(
                                translations.office.professional_caregiver.settings.quick_access
                                  .no_permissions
                              ),
                              callback: () => {
                                if (tableRow['name'].row.id) onDeactivate(tableRow['name'].row.id);
                              },
                            },
                          ],
                          wrapperSX: {
                            color: 'var(--dcm-gray-light)',
                          },
                          triggerSX: {width: '28px', height: '28px'},
                        }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
